// Roof pitch math: pitch (X/12) + span → rise, run, rafter length, angle.
// Used by the Roof Pitch Calculator. Standard right-triangle trigonometry —
// the rafter is the hypotenuse of (rise, run). An overhang multiplies the
// horizontal projection by the slope factor to get the extra rafter length.
import { round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface RoofPitchInputs {
  spanFt: number              // total horizontal span of the building (eave to eave)
  pitch: number               // rise per 12 in of run (e.g. 6 = 6/12)
  overhangIn: number          // horizontal eave overhang in inches
}

export interface RoofPitchResult {
  runFt: number               // half of span
  riseFt: number              // vertical rise at the ridge
  rafterBodyFt: number        // ridge to plate, no overhang
  rafterTotalFt: number       // includes overhang projection
  angleDeg: number            // roof angle from horizontal
  slopeFactor: number         // sqrt(pitch^2 + 144) / 12 (multiplier for converting horizontal → slope)
  pitchPercent: number        // pitch as a percentage grade
  lines: AccessoryLine[]
}

export const DEFAULT_ROOF_PITCH: RoofPitchInputs = {
  spanFt: 28,
  pitch: 6,
  overhangIn: 18,
}

export function computeRoofPitch(i: RoofPitchInputs, locale: Locale = 'en'): RoofPitchResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const span = Math.max(0, i.spanFt)
  const pitch = Math.max(0, i.pitch)
  const overhangFt = Math.max(0, i.overhangIn) / 12

  const run = span / 2
  const rise = (pitch / 12) * run
  const rafterBody = Math.sqrt(run * run + rise * rise)
  const slopeFactor = Math.sqrt(pitch * pitch + 144) / 12
  const rafterTotal = rafterBody + overhangFt * slopeFactor
  const angle = (Math.atan2(pitch, 12) * 180) / Math.PI
  const pitchPct = (pitch / 12) * 100

  const lines: AccessoryLine[] = []

  if (span > 0) {
    lines.push({
      item: 'speed square swanson rafter framing',
      label: tr('Speed square / rafter square', 'Équerre rapide / équerre de chevron'),
      kind: 'tool',
      note: tr('Marks rafter cuts directly from the pitch — the fastest way to lay out birdsmouths and plumb cuts.', 'Trace les coupes de chevron directement à partir de la pente — la méthode la plus rapide pour tracer les pieds de chevron et les coupes d’aplomb.'),
    })
    lines.push({
      item: 'framing nails 16d 3-1/2 galvanized',
      label: tr('Framing nails', 'Clous de charpente'),
      kind: 'consumable',
      qty: 1,
      unit: 'box',
      note: tr('16d 3-1/2 in galvanized — for nailing rafters to the top plate and ridge board.', '16d 3-1/2 po galvanisés — pour clouer les chevrons à la sablière et à la poutre faîtière.'),
    })
    lines.push({
      item: 'framing nailer pneumatic 21 degree',
      label: tr('Framing nailer', 'Cloueuse de charpente'),
      kind: 'tool',
      optional: true,
      note: tr('Pneumatic framer dramatically speeds up rafter installation — rent for a weekend if you don’t own one.', 'La cloueuse pneumatique accélère énormément la pose des chevrons — louez-la pour un weekend si vous n’en possédez pas.'),
    })
    lines.push({
      item: 'rafter hangers galvanized hurricane ties',
      label: tr('Rafter ties / hurricane clips', 'Étriers de chevron / attaches d’ouragan'),
      kind: 'consumable',
      qty: Math.max(2, Math.ceil((span * 2) / 1.33)),
      unit: 'piece',
      note: tr('One per rafter on both bearing walls — code-required in most jurisdictions to resist uplift.', 'Un par chevron sur les deux murs porteurs — exigé par le code dans la plupart des juridictions pour résister à l’arrachement.'),
    })
    lines.push({
      item: 'long level 4 foot construction',
      label: tr('4-ft level', 'Niveau de 4 pi'),
      kind: 'tool',
      optional: true,
      note: tr('A long level confirms the ridge runs true and the rafters are plumb.', 'Un niveau long confirme que le faîte est droit et que les chevrons sont d’aplomb.'),
    })
  }

  return {
    runFt: round(run, 3),
    riseFt: round(rise, 3),
    rafterBodyFt: round(rafterBody, 3),
    rafterTotalFt: round(rafterTotal, 3),
    angleDeg: round(angle, 2),
    slopeFactor: round(slopeFactor, 4),
    pitchPercent: round(pitchPct, 1),
    lines,
  }
}

/** Convert a decimal foot value to feet + inches + fraction (1/16ths). */
export function ftToFtInStr(decimalFt: number): string {
  if (!Number.isFinite(decimalFt) || decimalFt <= 0) return '0 ft'
  const totalInches = decimalFt * 12
  const ft = Math.floor(totalInches / 12)
  const remIn = totalInches - ft * 12
  const wholeIn = Math.floor(remIn)
  const sixteenths = Math.round((remIn - wholeIn) * 16)
  // Normalize 16/16 → bump whole inch
  let inches = wholeIn
  let frac = sixteenths
  let feet = ft
  if (frac === 16) { inches += 1; frac = 0 }
  if (inches === 12) { feet += 1; inches = 0 }
  const fracStr = frac === 0
    ? ''
    : ' ' + reduceSixteenths(frac)
  return `${feet} ft ${inches}${fracStr} in`
}

function reduceSixteenths(n: number): string {
  // n is 1..15
  let num = n
  let den = 16
  while (num % 2 === 0 && den % 2 === 0) { num /= 2; den /= 2 }
  return `${num}/${den}`
}
